import React, {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {backButton, mainButton} from "@telegram-apps/sdk-react";
import {Snackbar} from "@telegram-apps/telegram-ui";
import {useTranslation} from "react-i18next";
import {haptic} from "@/helpers/haptic.ts";

interface NavigationContextInterface {
  navigateToRoute: (route: string, replace?: boolean | undefined) => void;
  navigateBack: (replace?: boolean | undefined) => void;
  showSnackbar: (emoji: string, messageKey: string) => void;
}

const NavigationContext = createContext<NavigationContextInterface>({
  navigateToRoute: () => {
  },
  navigateBack: () => {
  },
  showSnackbar: () => {
  },
});

export const NavigationProvider = ({ children }:{children: ReactNode}) => {
  const [ navigationCounter, setNavigationCounter ] = useState<number>(0);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<React.ReactNode | null>(null);
  const { t } = useTranslation();

  const navigateToRoute = (route: string, replace: boolean | undefined) => {
    if (!replace) {
      haptic.impact('light');
    }
    mainButton.setParams({
      isVisible: false,
    });
    if (!replace) {
      setNavigationCounter((counter) => counter + 1);
    }
    navigate(route, {replace});
  }

  const showSnackbar = (emoji: string, messageKey: string) => {
    const message = t(messageKey);
    setSnackbar(<Snackbar className={"z-50"} before={emoji} onClose={()=>{setSnackbar(null)}} duration={3000}>{message}</Snackbar>)
  }

  const navigateBack = (replace?: boolean| undefined) => {
    if (!replace) {
      haptic.impact('light');
    }
    mainButton.setParams({
      isVisible: false,
    });
    setNavigationCounter((counter) => counter - 1);
    // @ts-expect-error it works
    navigate(-1, {replace: replace});
  }

  useEffect(() => {
    if (navigationCounter > 0) {
      backButton.show();
      return backButton.onClick(navigateBack);
    }
    backButton.hide();
  }, [navigationCounter]);

  return (
    <NavigationContext.Provider value={{navigateToRoute, navigateBack, showSnackbar}}>
      {children}
      {snackbar}
    </NavigationContext.Provider>
  );
}

export const useNavigation = (): NavigationContextInterface => {
  const context = useContext(NavigationContext);

  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }

  return context;
};
